import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * Global "are you sure you want to delete?" confirmation system.
 *
 * Usage from any component:
 *   const confirmDelete = useConfirmDelete();
 *   const ok = await confirmDelete({
 *     title: 'Eliminar archivo',
 *     itemName: 'IMG_1234.jpg',
 *     description: 'Se borrará del servidor de forma irreversible.',
 *   });
 *   if (!ok) return;
 *
 * If `requireReason: true` is passed the user MUST type a reason
 * (>=10 chars) and the result is `{confirmed:true, reason:'...'}`.
 *
 * Replaces `window.confirm()` with a styled, on-brand dialog matching
 * `DeleteNoteDialog.jsx` (red header, warning box, clear destructive CTA).
 */

const ConfirmDeleteContext = createContext(null);

const DEFAULTS = {
  title: 'Eliminar elemento',
  description: 'Esta acción es permanente y no se puede deshacer.',
  warning: null,
  itemName: '',
  itemSubtitle: '',
  confirmText: 'Eliminar',
  cancelText: 'Cancelar',
  requireReason: false,
  reasonPlaceholder: 'Explique brevemente el motivo de la eliminación…',
  reasonMinLength: 10,
};

export function ConfirmDeleteProvider({ children }) {
  const [opts, setOpts] = useState(null); // null when closed
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setOpts(null);
    setReason('');
    setBusy(false);
  }, []);

  const confirmDelete = useCallback((overrides = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setReason('');
      setOpts({ ...DEFAULTS, ...overrides });
    });
  }, []);

  const handleConfirm = () => {
    if (!opts) return;
    if (opts.requireReason && reason.trim().length < opts.reasonMinLength) return;
    setBusy(true);
    close(opts.requireReason ? { confirmed: true, reason: reason.trim() } : true);
  };

  const handleCancel = () => {
    close(opts?.requireReason ? { confirmed: false, reason: '' } : false);
  };

  return (
    <ConfirmDeleteContext.Provider value={confirmDelete}>
      {children}
      <Dialog open={!!opts} onOpenChange={(v) => { if (!v) handleCancel(); }}>
        <DialogContent
          className="max-w-md"
          data-testid="confirm-delete-dialog"
          onEscapeKeyDown={handleCancel}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 size={20} />
              {opts?.title}
            </DialogTitle>
            {opts?.description && (
              <DialogDescription>{opts.description}</DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">
                <strong>Atención:</strong>{' '}
                {opts?.warning ||
                  'Esta acción es permanente y no se puede deshacer.'}
              </p>
            </div>

            {opts?.itemName && (
              <div className="p-3 bg-gray-50 rounded-lg overflow-hidden" data-testid="confirm-delete-item">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Elemento a eliminar
                </p>
                <p className="font-medium mt-1 break-all" style={{ overflowWrap: 'anywhere' }}>{opts.itemName}</p>
                {opts.itemSubtitle && (
                  <p className="text-xs text-gray-500 mt-1 break-all" style={{ overflowWrap: 'anywhere' }}>{opts.itemSubtitle}</p>
                )}
              </div>
            )}

            {opts?.requireReason && (
              <div className="space-y-2">
                <Label htmlFor="confirm-delete-reason" className="text-sm font-medium">
                  Motivo de eliminación <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="confirm-delete-reason"
                  data-testid="confirm-delete-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={opts.reasonPlaceholder}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-gray-500">
                  {reason.length}/{opts.reasonMinLength} caracteres mínimos
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={busy}
              data-testid="confirm-delete-cancel"
            >
              {opts?.cancelText}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={
                busy ||
                (opts?.requireReason && reason.trim().length < (opts?.reasonMinLength || 10))
              }
              data-testid="confirm-delete-confirm"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Eliminando…
                </>
              ) : (
                <>
                  <Trash2 size={14} className="mr-2" />
                  {opts?.confirmText}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmDeleteContext.Provider>
  );
}

export function useConfirmDelete() {
  const ctx = useContext(ConfirmDeleteContext);
  if (!ctx) {
    // Safety fallback — if the provider isn't mounted, fall back to native
    // confirm so we never silently skip the destructive guard.
    return (opts = {}) =>
      Promise.resolve(
        // eslint-disable-next-line no-alert
        window.confirm(
          `${opts.title || 'Eliminar'}\n\n${opts.description || ''}\n\n¿Continuar?`,
        ),
      );
  }
  return ctx;
}
